import * as React from 'react';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';
import { TypographyCaption, TypographySmall } from '@/components/ui/typography';

type LinkItemProps = {
  label: string;
  description?: string;
  icon: React.ReactNode;
  href: string;
  external?: boolean;
  ref?: React.Ref<HTMLAnchorElement>;
} & Omit<React.ComponentPropsWithoutRef<'a'>, 'href' | 'ref'>;

/**
 * A single menu row — icon tile + label (+ optional description). Renders an
 * internal next-intl <Link> (locale-prefixed) or a plain external <a>. Designed
 * to be the child of <NavigationMenuLink asChild>, which merges focus/active
 * props in, so it forwards `ref` and any extra props to the anchor.
 *
 * The icon is cloned with an explicit `text-primary` so it keeps the brand
 * green — NavigationMenuLink otherwise paints descendant svgs muted.
 */
export function LinkItem({
  label,
  description,
  icon,
  href,
  external,
  className,
  ref,
  ...props
}: LinkItemProps) {
  const classes = cn(
    'flex flex-row items-center gap-x-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring',
    className,
  );

  const styledIcon = React.isValidElement<{ className?: string }>(icon)
    ? React.cloneElement(icon, {
        className: cn('size-5 text-primary', icon.props.className),
      })
    : icon;

  const content = (
    <>
      <span className="flex aspect-square size-11 shrink-0 items-center justify-center rounded-md border bg-card text-primary shadow-xs">
        {styledIcon}
      </span>
      <span className="flex flex-col items-start justify-center">
        <TypographySmall className="text-foreground">{label}</TypographySmall>
        {description ? (
          <TypographyCaption className="line-clamp-2 text-muted-foreground">
            {description}
          </TypographyCaption>
        ) : null}
      </span>
    </>
  );

  if (external) {
    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <Link ref={ref} href={href} className={classes} {...props}>
      {content}
    </Link>
  );
}
